import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { useAuth } from './AuthContext';

export type ThemeMode = 'light' | 'dark' | 'orange' | 'slate';

interface ThemeContextType {
  themeMode: ThemeMode;
  setThemeMode: (mode: ThemeMode) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [themeMode, setThemeModeState] = useState<ThemeMode>(() => {
    // Try to get user-specific theme first, then fall back to general theme
    const userThemeKey = user?.id ? `theme-mode-${user.id}` : 'theme-mode';
    const savedTheme = localStorage.getItem(userThemeKey) || localStorage.getItem('theme-mode');
    return (savedTheme as ThemeMode) || 'orange';
  });

  const setThemeMode = useCallback((mode: ThemeMode) => {
    setThemeModeState(mode);
    // Save theme preference per user if logged in
    const themeKey = user?.id ? `theme-mode-${user.id}` : 'theme-mode';
    localStorage.setItem(themeKey, mode);
    // Also save as general fallback
    localStorage.setItem('theme-mode', mode);
  }, [user?.id]);

  const toggleTheme = useCallback(() => {
    setThemeModeState(prevMode => {
      const nextMode: ThemeMode = prevMode === 'light' ? 'dark' : prevMode === 'dark' ? 'orange' : prevMode === 'orange' ? 'slate' : 'light';
      
      // Save theme preference
      const themeKey = user?.id ? `theme-mode-${user.id}` : 'theme-mode';
      localStorage.setItem(themeKey, nextMode);
      localStorage.setItem('theme-mode', nextMode);
      
      return nextMode;
    });
  }, [user?.id]);

  useEffect(() => {
    // Apply theme class to document body for global styling
    document.body.className = `theme-${themeMode}`;
  }, [themeMode]);

  useEffect(() => {
    // Update theme when user changes (login/logout)
    if (user?.id) {
      const userThemeKey = `theme-mode-${user.id}`;
      const userSavedTheme = localStorage.getItem(userThemeKey);
      if (userSavedTheme && userSavedTheme !== themeMode) {
        setThemeModeState(userSavedTheme as ThemeMode);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const value = useMemo(() => ({
    themeMode,
    setThemeMode,
    toggleTheme
  }), [themeMode, setThemeMode, toggleTheme]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};


export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
